/**
 * Shares Contract — The typed API contract for the Shares domain.
 *
 * All response types are derived from the OpenAPI spec (backend is authoritative).
 * Generated types come from `pnpm generate:api` → `zod.gen.ts`.
 * Request interfaces are UI-side only (camelCase → the adapter maps to snake_case).
 *
 * SECURITY MODEL:
 * - Share tokens are 128-bit cryptographically random, base62-encoded (22 chars)
 * - Optional password protection (bcrypt-hashed server-side, never returned to client)
 * - Link access levels: disabled | viewer | commenter | editor
 * - Time-bounded with configurable expiration
 * - Revocable at any time by the creator or workspace admin
 * - Access counter for audit trail
 * - Rate-limited resolution to prevent enumeration attacks
 */

import { z } from 'zod';
import type { ApiResponse } from './shared';

import {
  zSharingConfigResponse as GeneratedSharingConfigResponse,
  zPublicSharingResponse as GeneratedPublicSharingResponse,
  zCollaboratorResponse as GeneratedCollaboratorResponse,
  zSharedContentData as GeneratedSharedContentData,
  zSharedContentResponse as GeneratedSharedContentResponse,
  zSendShareResponse as GeneratedSendShareResponse,
  zSendRecipientResult as GeneratedSendRecipientResult,
} from '@/api/generated/hey-api/zod.gen';

// ─── Enums ─────────────────────────────────────────────────────────────

export const ShareResourceTypeEnum = z.enum([
  'document',
  'libraryItem',
  'session',
  'draft',
  'citation',
  'note',
  'collection',
]);
export type ShareResourceType = z.infer<typeof ShareResourceTypeEnum>;

export const ShareStatusEnum = z.enum(['active', 'expired', 'revoked']);
export type ShareStatus = z.infer<typeof ShareStatusEnum>;

// ─── DTOs (derived from generated schemas) ─────────────────────────────

/** Canonical sharing config — returned by POST /shares, GET /shares/:id, PUT /shares/:id/link */
export const SharingConfigSchema = GeneratedSharingConfigResponse;
export type SharingConfig = z.infer<typeof SharingConfigSchema>;

/** Public share metadata — returned by GET /shares/resolve/:token */
export const PublicSharingSchema = GeneratedPublicSharingResponse;
export type PublicSharing = z.infer<typeof PublicSharingSchema>;

/** Collaborator on a shared resource */
export const CollaboratorSchema = GeneratedCollaboratorResponse;
export type Collaborator = z.infer<typeof CollaboratorSchema>;

// Back-compat aliases used across the codebase
export const ShareDTOSchema = GeneratedSharingConfigResponse;
export type ShareDTO = SharingConfig;

// ─── Request Types ─────────────────────────────────────────────────────

/** POST /shares — get or create sharing config */
export interface CreateShareRequest {
  resourceId: string;
  resourceType: ShareResourceType;
  resourceTitle?: string;
}

/** PUT /shares/:id/link — update link access settings */
export interface UpdateShareRequest {
  accessLevel?: string;
  password?: string | null;
  expiresInHours?: number | null;
  maxAccessCount?: number | null;
  allowLibrarySave?: boolean;
  showAnnotations?: boolean;
}

/** POST /shares/:id/verify-password */
export interface VerifySharePasswordRequest {
  password: string;
}

/** Query params for GET /shares */
export interface ShareListFilters {
  resourceId?: string;
  resourceType?: ShareResourceType;
  status?: ShareStatus;
}

export type ShareSortField = 'createdAt' | 'expiresAt' | 'accessCount';

export interface ShareListParams extends ShareListFilters {
  sortBy?: ShareSortField;
  sortDirection?: 'asc' | 'desc';
}

// ─── Response Types ────────────────────────────────────────────────────

/** POST /shares → flat SharingConfigResponse (shareUrl populated on first creation) */
export type CreateShareResponse = SharingConfig;

/** GET /shares → list */
export type ShareListResponse = ApiResponse<SharingConfig[]>;

/** GET /shares/:id → single config */
export type ShareDetailResponse = SharingConfig;

/** PUT /shares/:id/link → updated config */
export type UpdateShareResponse = SharingConfig;

/** DELETE /shares/:id/collaborators/:id → 204 */
export type RevokeShareResponse = void;

/** POST /shares/:id/verify-password */
export type VerifyPasswordResponse = ApiResponse<{
  valid: boolean;
  accessToken?: string;
}>;

// ─── Shared Content Types ─────────────────────────────────────────────

export const SharedContentDataSchema = GeneratedSharedContentData;

/** Share highlight from the sharer's annotations (PII-stripped by backend) */
export interface ShareHighlight {
  id: string;
  highlightType: string;
  color: string;
  pageNumber: number;
  selectedText: string;
  comment: string | null;
  rects: Array<{ x: number; y: number; width: number; height: number }>;
}

export type SharedContentData = z.infer<typeof SharedContentDataSchema>;

export const SharedContentResponseSchema = GeneratedSharedContentResponse;
export type SharedContentResponse = z.infer<typeof SharedContentResponseSchema>;

// ─── Send to People Types ─────────────────────────────────────────────

export interface SendShareRecipient {
  email: string;
  name?: string;
}

export interface SendSharePeopleRequest {
  shareUrl: string;
  recipients: SendShareRecipient[];
  message?: string;
}

export const SendRecipientResultSchema = GeneratedSendRecipientResult;
export type SendShareRecipientResult = z.infer<typeof SendRecipientResultSchema>;

export const SendShareResponseSchema = GeneratedSendShareResponse;
export type SendSharePeopleResponse = z.infer<typeof SendShareResponseSchema>;

// ─── Adapter Interface ─────────────────────────────────────────────────

export interface ISharesAdapter {
  /** Get or create sharing config for a resource */
  create(data: CreateShareRequest): Promise<CreateShareResponse>;

  /** List shares for the current workspace */
  list(params?: ShareListParams): Promise<ShareListResponse>;

  /** Get a share by its internal ID */
  getById(id: string): Promise<ShareDetailResponse>;

  /** Resolve a share by its public token (for /share/:token page) */
  resolveByToken(token: string): Promise<ShareDetailResponse>;

  /** Update link access settings */
  update(id: string, data: UpdateShareRequest): Promise<UpdateShareResponse>;

  /** Revoke a share (remove collaborator) */
  revoke(id: string): Promise<RevokeShareResponse>;

  /** Verify password for a password-protected share */
  verifyPassword(id: string, data: VerifySharePasswordRequest): Promise<VerifyPasswordResponse>;

  /** Record a share access (fire-and-forget) */
  recordAccess(id: string): Promise<void>;

  /** Fetch resource content for a shared resource */
  getContent(token: string, accessToken?: string): Promise<SharedContentResponse>;

  /** Send a share link to specific people */
  sendToPeople(shareId: string, data: SendSharePeopleRequest): Promise<SendSharePeopleResponse>;
}

// ─── Share Comment Types ───────────────────────────────────────────────

/** A comment on a shared resource. Auth required. */
export interface ShareComment {
  id: string;
  sharingId: string;
  content: string;
  authorName: string;
  authorId: string;
  parentId: string | null;
  pageNumber: number | null;
  replyCount: number;
  createdAt: string;
  updatedAt: string;
}

/** POST /shares/resolve/:token/comments */
export interface CreateShareCommentRequest {
  content: string;
  parentId?: string | null;
  pageNumber?: number | null;
}

/** PATCH /shares/resolve/:token/comments/:id */
export interface UpdateShareCommentRequest {
  content: string;
}

export interface ShareCommentsResponse {
  data: ShareComment[];
  total: number;
  hasMore: boolean;
}

// ─── Save to Library Types ─────────────────────────────────────────────

/** POST /shares/resolve/:token/save-to-library */
export interface SaveFromShareRequest {
  processingMode: 'metadata' | 'full';
  targetWorkspaceId?: string | null;
}

export interface SaveFromShareResponse {
  documentId: string | null;
  title: string | null;
  processingMode: string;
  hasPdf: boolean;
}
